import isEqual from "lodash/isEqual";
import UserNotification from "util/UserNotification";
import { isInfiniteScrollUp } from "logview/helpers";
const MAX_VISIBLE_PAGES = 2;
const _onAppendLoadedMessages = ({
  internalListState,
  loadedAllPrevMessages,
  pageRefs,
  updateListState,
  infiniteScrollDirection
}) => (newAfter, newMessages) => {
  if (internalListState.after && !isEqual(internalListState.after, newAfter)) {
    if (newMessages.length === 0) {
      updateListState(
        { ...internalListState, after: newAfter },
        { direction: void 0, targetPage: void 0, targetPageOffsetTop: void 0, loadedAllPrevMessages }
      );
      return;
    }
    const topVisiblePage = internalListState.visiblePagesIds.max();
    const bottomVisiblePage = internalListState.visiblePagesIds.min();
    const newPageIndex = topVisiblePage + 1;
    let newVisiblePagesIds = internalListState.visiblePagesIds.add(newPageIndex);
    if (newPageIndex - bottomVisiblePage >= MAX_VISIBLE_PAGES) {
      newVisiblePagesIds = newVisiblePagesIds.delete(bottomVisiblePage);
    }
    const scrollPositionUpdate = {
      direction: isInfiniteScrollUp(infiniteScrollDirection) ? "up" : "down",
      targetPage: newPageIndex - 1,
      targetPageOffsetTop: pageRefs.current[newPageIndex - 1].offsetTop,
      loadedAllPrevMessages
    };
    updateListState(
      {
        ...internalListState,
        after: newAfter,
        loadedPages: { ...internalListState.loadedPages, [newPageIndex]: newMessages },
        loadedMessagesCount: internalListState.loadedMessagesCount + newMessages.length,
        visiblePagesIds: newVisiblePagesIds
      },
      scrollPositionUpdate
    );
  }
};
const _requestPrevPage = ({
  onLoadMessages,
  appendLoadedMessages,
  internalListState,
  loadPrevPromiseRef
}) => {
  if (!loadPrevPromiseRef.current) {
    if (internalListState.after) {
      loadPrevPromiseRef.current = onLoadMessages(internalListState.after).then(({ after, messages }) => {
        appendLoadedMessages(after, messages);
      }).catch((error) => {
        UserNotification.error(`Fetching previous log messages failed with status: ${error}`);
      }).finally(() => {
        loadPrevPromiseRef.current = void 0;
      });
    } else {
      throw Error('Can not reexecute search, because parameter "after" is missing.');
    }
  }
};
const _prevPageIsInState = (listState) => {
  const topVisiblePage = listState.visiblePagesIds.max();
  const newPageIndex = topVisiblePage + 1;
  return !!listState.loadedPages[newPageIndex];
};
const _loadPrevPageFromState = ({
  internalListState,
  loadedAllPrevMessages,
  pageRefs,
  updateListState,
  infiniteScrollDirection
}) => {
  const topVisiblePage = internalListState.visiblePagesIds.max();
  const bottomVisiblePage = internalListState.visiblePagesIds.min();
  const newTopPageIndex = topVisiblePage + 1;
  let newVisiblePagesIds = internalListState.visiblePagesIds.add(newTopPageIndex);
  if (newTopPageIndex - bottomVisiblePage >= MAX_VISIBLE_PAGES) {
    newVisiblePagesIds = newVisiblePagesIds.delete(bottomVisiblePage);
  }
  const scrollPositionUpdate = {
    direction: isInfiniteScrollUp(infiniteScrollDirection) ? "up" : "down",
    targetPage: newTopPageIndex - 1,
    targetPageOffsetTop: pageRefs.current[newTopPageIndex - 1].offsetTop,
    loadedAllPrevMessages
  };
  updateListState(
    {
      ...internalListState,
      visiblePagesIds: newVisiblePagesIds
    },
    scrollPositionUpdate
  );
};
const _loadNextPageFromState = ({
  internalListState,
  loadedAllPrevMessages,
  pageRefs,
  updateListState,
  infiniteScrollDirection
}) => {
  const topVisiblePage = internalListState.visiblePagesIds.max();
  const bottomVisiblePage = internalListState.visiblePagesIds.min();
  if (bottomVisiblePage <= 1) {
    return;
  }
  const newBottomPageIndex = bottomVisiblePage - 1;
  let newVisiblePagesIds = internalListState.visiblePagesIds.add(newBottomPageIndex);
  if (topVisiblePage - newBottomPageIndex >= MAX_VISIBLE_PAGES) {
    newVisiblePagesIds = newVisiblePagesIds.delete(topVisiblePage);
  }
  const scrollPositionUpdate = {
    direction: isInfiniteScrollUp(infiniteScrollDirection) ? "down" : "up",
    targetPage: bottomVisiblePage,
    targetPageOffsetTop: pageRefs.current[bottomVisiblePage].offsetTop,
    loadedAllPrevMessages
  };
  updateListState(
    {
      ...internalListState,
      visiblePagesIds: newVisiblePagesIds
    },
    scrollPositionUpdate
  );
};
export const loadPrevPage = ({
  finishedScrollPositionUpdateRef,
  internalListState,
  loadedAllPrevMessages,
  loadPrevPromiseRef,
  pageRefs,
  updateListState,
  onLoadMessages,
  infiniteScrollDirection
}) => {
  if (finishedScrollPositionUpdateRef.current && !loadPrevPromiseRef.current) {
    const prevPageIsInState = _prevPageIsInState(internalListState);
    if (prevPageIsInState) {
      _loadPrevPageFromState({
        internalListState,
        pageRefs,
        updateListState,
        loadedAllPrevMessages,
        infiniteScrollDirection
      });
    }
    if (!prevPageIsInState && !loadedAllPrevMessages) {
      const _appendLoadedMessages = _onAppendLoadedMessages({
        loadedAllPrevMessages,
        internalListState,
        pageRefs,
        updateListState,
        infiniteScrollDirection
      });
      _requestPrevPage({
        onLoadMessages,
        appendLoadedMessages: _appendLoadedMessages,
        internalListState,
        loadPrevPromiseRef
      });
    }
  }
};
export const loadNextPage = ({
  internalListState,
  pageRefs,
  updateListState,
  finishedScrollPositionUpdateRef,
  loadedAllPrevMessages,
  infiniteScrollDirection
}) => {
  if (finishedScrollPositionUpdateRef.current) {
    _loadNextPageFromState({
      loadedAllPrevMessages,
      internalListState,
      pageRefs,
      updateListState,
      infiniteScrollDirection
    });
  }
};
